'use client';

import { memo, useCallback } from 'react';

import { useToasts } from '@air/provider-toast';
import { cn } from '@air/utils-tailwind';

import { Button } from '~/components/Button';
import { useVerifyEmailResend } from '~/hooks/useVerifyEmailResend';

export type VerifyUserProps = {
  email: string;
  isRefreshing: boolean;
  onLogout: () => void;
  onRefresh: () => void;
};

export const VerifyUser = memo(({ email, isRefreshing, onLogout, onRefresh }: VerifyUserProps) => {
  const { showToast } = useToasts();
  const { isPending, isSuccess, mutate } = useVerifyEmailResend();

  const handleRefresh = useCallback(() => {
    if (isRefreshing) {
      return;
    }

    onRefresh();
  }, [isRefreshing, onRefresh]);

  const handleResend = useCallback(() => {
    if (isPending || isSuccess) {
      return;
    }

    mutate(undefined, {
      onError: () => {
        showToast('Failed to resend verification email', { color: 'red' });
      },
    });
  }, [isPending, isSuccess, mutate, showToast]);

  let resendButtonLabel = 'Resend email';

  if (isPending) {
    resendButtonLabel = 'Sending…';
  } else if (isSuccess) {
    resendButtonLabel = 'Sent!';
  }

  return (
    <div className="flex flex-col gap-4" data-testid="VERIFY_USER">
      <p className="text-center text-14 text-neutral-11">
        We sent an email to {email}. Click the link inside to get started. Don&apos;t see it? Check your spam folder.
      </p>
      <Button
        appearance="primary"
        className="w-full"
        data-testid="VERIFY_USER_RESEND_BUTTON"
        disabled={isPending || isSuccess}
        isLoading={isPending}
        onClick={handleResend}
        size="large"
        type="button"
      >
        {resendButtonLabel}
      </Button>
      <Button
        appearance="outline"
        className="w-full"
        data-testid="VERIFY_USER_REFRESH_BUTTON"
        disabled={isRefreshing}
        isLoading={isRefreshing}
        onClick={handleRefresh}
        size="large"
        type="button"
      >
        Refresh
      </Button>
      <p className="text-center text-12 text-neutral-11">
        Wrong email?{' '}
        <button
          className={cn(
            'rounded-2 font-medium text-brand-9 underline',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-9',
          )}
          data-testid="VERIFY_USER_LOGOUT_BUTTON"
          onClick={onLogout}
          type="button"
        >
          Use a different account
        </button>
      </p>
    </div>
  );
});

VerifyUser.displayName = 'VerifyUser';
